import {handleFormError, handleSubmit} from 'app/client/lib/formUtils';
import {makeT} from 'app/client/lib/localization';
import {DiscordAllowlistModel} from 'app/client/models/DiscordAllowlistModel';
import {textInput} from 'app/client/ui/inputs';
import {bigPrimaryButton} from 'app/client/ui2018/buttons';
import {theme} from 'app/client/ui2018/cssVars';
import {icon} from 'app/client/ui2018/icons';
import {loadingSpinner} from 'app/client/ui2018/loaders';
import {confirmModal, cssModalTitle, modal} from 'app/client/ui2018/modals';
import {Computed, Disposable, dom, makeTestId, Observable, styled} from 'grainjs';

const t = makeT('DiscordAllowlistConfig');

const testId = makeTestId('test-discord-allowlist-');

const DISCORD_ID_RE = /^\d{17,21}$/;

export class DiscordAllowlistConfig extends Disposable {
  constructor(private _model: DiscordAllowlistModel) {
    super();
  }

  public buildDom() {
    return dom.domComputed(this._model.entries, (entries) => {
      if (entries === null) {
        return cssLoadingSpinner(loadingSpinner());
      }
      return dom('div',
        cssParagraph(
          t(
            'Allowlist of Discord user IDs that may sign in even if they are not members ' +
            'of the required Discord server. Right-click your name in Discord (with ' +
            'Developer Mode enabled) and pick "Copy User ID" to get an ID.'
          ),
        ),
        dom('div',
          dom.hide(entries.length === 0),
          cssSectionHeading(t('Allowed users')),
          cssEntries(
            dom.forEach(entries, (entry) =>
              cssEntry(
                cssEntryId(entry.discordUserId, testId('entry-id')),
                cssEntryNote(entry.note || '', testId('entry-note')),
                cssEntryDelete(
                  icon('Remove'),
                  dom.on('click', () => this._handleRemoveClick(entry.discordUserId)),
                  testId('entry-delete'),
                ),
                testId('entry'),
              )
            )
          )
        ),
        bigPrimaryButton(
          entries.length === 0 ? t('Add first user') : t('Add user'),
          dom.on('click', () => this._handleAddClick()),
          testId('add'),
        ),
      );
    });
  }

  private _handleAddClick() {
    showAddForm({
      onSubmit: (entry) => this._model.add(entry),
    });
  }

  private _handleRemoveClick(discordUserId: string) {
    confirmModal(
      t('Remove from allowlist?'),
      t('Remove'),
      () => this._model.remove(discordUserId),
      {
        explanation: t(
          'Discord user {{id}} will no longer bypass the guild-membership check.',
          {id: discordUserId},
        ),
      },
    );
  }
}

interface AddFormOptions {
  onSubmit(entry: {discordUserId: string; note?: string}): Promise<void>;
}

function showAddForm({onSubmit}: AddFormOptions) {
  return modal((ctl, owner) => {
    const discordUserId = Observable.create(owner, '');
    const note = Observable.create(owner, '');
    const pending = Observable.create(owner, false);
    const error = Observable.create(owner, '');
    const disabled = Computed.create(owner, use => !DISCORD_ID_RE.test(use(discordUserId).trim()));

    return [
      cssAddModal.cls(''),
      cssModalTitle(t('Add Discord user to allowlist')),
      dom('form',
        handleSubmit({
          pending,
          disabled,
          onSubmit: () => onSubmit({
            discordUserId: discordUserId.get().trim(),
            note: note.get().trim() || undefined,
          }),
          onSuccess: () => ctl.close(),
          onError: (e) => handleFormError(e, error),
        }),
        cssLabelAndInput(
          cssLabel(t('Discord User ID')),
          textInput(discordUserId, {
            placeholder: t('e.g. 1475123091716968640'),
            type: 'text',
            autocomplete: 'off',
          }, testId('input-id')),
          cssHelp(t('17 to 21 digits — the snowflake ID from Discord developer mode.')),
        ),
        cssLabelAndInput(
          cssLabel(t('Note (optional)')),
          textInput(note, {
            placeholder: t('e.g. discord handle'),
            type: 'text',
            autocomplete: 'off',
          }, testId('input-note')),
        ),
        dom.maybe(error, (msg) => cssError(msg, testId('error'))),
        cssActions(
          dom('button',
            {type: 'button', class: 'gristlabs-large-basic-button'},
            t('Cancel'),
            dom.on('click', () => ctl.close()),
          ),
          dom('button',
            {type: 'submit', class: 'gristlabs-large-primary-button'},
            dom.prop('disabled', disabled),
            t('Add'),
            testId('submit'),
          ),
        ),
      ),
    ];
  });
}

const cssLoadingSpinner = styled('div', `
  display: flex;
  justify-content: center;
  padding: 24px;
`);

const cssParagraph = styled('p', `
  color: ${theme.text};
  margin: 0 0 16px 0;
  line-height: 1.5;
`);

const cssSectionHeading = styled('div', `
  font-weight: 600;
  margin-bottom: 8px;
  color: ${theme.text};
`);

const cssEntries = styled('div', `
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`);

const cssEntry = styled('div', `
  display: grid;
  grid-template-columns: minmax(180px, 240px) 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid ${theme.modalBorder};
  border-radius: 4px;
  background: ${theme.mainPanelBg};
`);

const cssEntryId = styled('div', `
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: ${theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`);

const cssAddModal = styled('div', `
  max-width: 480px;
`);

const cssEntryNote = styled('div', `
  color: ${theme.lightText};
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`);

const cssEntryDelete = styled('div', `
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  color: ${theme.lightText};
  &:hover {
    background: ${theme.hover};
    color: ${theme.text};
  }
`);

const cssLabelAndInput = styled('div', `
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`);

const cssLabel = styled('label', `
  font-weight: 600;
  color: ${theme.text};
`);

const cssHelp = styled('div', `
  color: ${theme.lightText};
  font-size: 12px;
`);

const cssError = styled('div', `
  color: ${theme.errorText};
  margin: 4px 0 8px;
`);

const cssActions = styled('div', `
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`);
